/**
 * Display copy for manifest field-issue rule keys.
 *
 * A `ManifestDiscrepancy` of kind `FIELD_ISSUE` carries a `ruleKey` naming the
 * validity rule that fired. Every surface that shows one of those rows to the
 * waste team renders it through {@link describeRuleKey}: the Field Issues pane,
 * the consolidated waste-stream Issues column and its tooltip, the per-cell
 * state waste code warning, and compliance-engine validation messages. Keeping
 * one registry means the same rule reads identically everywhere.
 *
 * State-waste-code rules were the first field issues to ship, so their copy
 * lives with the codes themselves in `stateWasteCodes.ts` and is imported here
 * per jurisdiction.
 * Rules from other domains register below. Anything unregistered falls back to
 * the raw key, which is legible enough to triage but is not display copy — add
 * an entry when a new rule starts reaching the UI.
 */
import { CA_STATE_WASTE_CODE_RULE_DESC } from "./caStateWasteCodeRules";
import { TX_STATE_WASTE_CODE_RULE_DESC } from "./stateWasteCodes";

/**
 * Field-issue rules that are not state-waste-code rules.
 *
 * Keys are written as literals rather than imported from the modules that
 * define them: those live in `encamp-waste-data-access`, which depends on this
 * package, so importing them would invert the dependency. A unit test beside
 * each rule pins its literal to the exported constant, so a rename fails a test
 * instead of silently regressing this copy.
 */
const OTHER_FIELD_ISSUE_RULE_DESC: Record<string, string> = {
  // encamp-waste-data-access → discrepancies/detectImplausibleSpecificGravity
  IMPLAUSIBLE_SPECIFIC_GRAVITY:
    "Specific gravity is physically impossible — likely a lbs/gal density",
};

const FIELD_ISSUE_RULE_DESC: Record<string, string> = {
  ...TX_STATE_WASTE_CODE_RULE_DESC,
  ...CA_STATE_WASTE_CODE_RULE_DESC,
  ...OTHER_FIELD_ISSUE_RULE_DESC,
};

/**
 * Rule keys whose stored source values are the codes that *triggered* the rule
 * rather than the offending value itself.
 *
 * Every field issue before the California rules stored the offending value — the malformed
 * TX code, the implausible density — so renderers could label it "Code" or
 * "Value" and be right. `CA_HAZARDOUS_NO_CA_CODE` fires on an *absence*, so it
 * has no offending value; it stores the federal/state codes that made a CA code
 * required. Labelling those "Code:" reads as though they are the defect, which
 * is the opposite of what the row means.
 *
 * Renderers consult {@link storesEvidenceValues} to word the values as context.
 */
const EVIDENCE_VALUE_RULE_KEYS: ReadonlySet<string> = new Set(["CA_HAZARDOUS_NO_CA_CODE"]);

/** `true` iff this rule's stored values are evidence, not the offending value. */
export function storesEvidenceValues(ruleKey: string | null | undefined): boolean {
  return ruleKey !== null && ruleKey !== undefined && EVIDENCE_VALUE_RULE_KEYS.has(ruleKey);
}

/**
 * Human-readable description for a field-issue rule key, falling back to the
 * raw key when the rule is not registered.
 */
export function describeRuleKey(ruleKey: string): string {
  return FIELD_ISSUE_RULE_DESC[ruleKey] ?? ruleKey;
}
